import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Modal from '@/components/Modal';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Chip from '@/components/Chip';
import LoadingState from '@/components/LoadingState';
import Button from '@/components/Button';
import { DocumentsAPI } from '@/services/documentsAPI';
import { Document, DocumentVersion } from '@/types/documents';

interface VersionsModalProps {
  document: Document;
  onClose: () => void;
}

const VersionsModal: React.FC<VersionsModalProps> = ({ document, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVersions();
  }, [document.id]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const response = await DocumentsAPI.getVersions(document.id);
      if (response.success) {
        setVersions(response.data);
      }
    } catch (err: any) {
      setError('Failed to load versions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      size="lg"
      title={`Versions - ${document.name}`}
      onRequestClose={onClose}
    >
      <Card.Section>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <Body>{error}</Body>
        ) : (
          <Stack gap={12} vertical>
            {versions.length === 0 ? (
              <Body>No versions found.</Body>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Version
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Signature Position
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Uploaded
                    </th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((version) => (
                    <tr
                      key={version.id}
                      style={{ borderBottom: '1px solid #eee' }}
                    >
                      <td style={{ padding: '8px' }}>
                        <Body weight="medium">v{version.version_number}</Body>
                      </td>
                      <td style={{ padding: '8px' }}>
                        {version.has_signature_coordinates ? (
                          <Stack gap={4} vertical>
                            <Chip color="success" label="Set" size="small" />
                            <Small>
                              {version.signature_fields?.length > 0 ? (
                                (() => {
                                  const sigCount =
                                    version.signature_fields.filter(
                                      (f) => f.field_type === 'signature',
                                    ).length;
                                  const dateCount =
                                    version.signature_fields.filter(
                                      (f) => f.field_type === 'date',
                                    ).length;
                                  const parts = [];
                                  if (sigCount > 0)
                                    parts.push(`${sigCount} signature`);
                                  if (dateCount > 0)
                                    parts.push(`${dateCount} date`);
                                  return parts.join(', ');
                                })()
                              ) : (
                                <>
                                  Page {version.signature_page}, (
                                  {version.signature_x?.toFixed(1)},{' '}
                                  {version.signature_y?.toFixed(1)})
                                </>
                              )}
                            </Small>
                          </Stack>
                        ) : (
                          <Chip label="Not set" size="small" />
                        )}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Small>
                          {version.created_at
                            ? format(
                                new Date(version.created_at),
                                'MMM dd, yyyy HH:mm',
                              )
                            : 'N/A'}
                        </Small>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <Button
                          a11yLabel={`Download version ${version.version_number}`}
                          appearance="outline"
                          label="Download"
                          onClick={() => {
                            if (version.download_url) {
                              window.open(version.download_url, '_blank');
                            }
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Stack>
        )}
      </Card.Section>
      <Card.Section subdued>
        <Stack justify="end">
          <Button
            a11yLabel="Close"
            appearance="outline"
            label="Close"
            onClick={onClose}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default VersionsModal;
